/*
 * Grabado personalizado — Mundo Patagón
 * -------------------------------------
 * Tres opciones, ninguna preseleccionada:
 *   - No personalizar  -> sin cargo
 *   - Grabar texto     -> cargo según el tag del producto (gratis / 5000 / 9900)
 *   - Grabar imagen    -> siempre +$9.900
 * Texto e imagen son excluyentes.
 *
 * - Bloquea el botón "Comprar" hasta que el cliente elige una opción.
 * - Valida el texto (no vacío, máx. 40 caracteres y 2 líneas) y la imagen
 *   (archivo obligatorio), con aviso en vivo.
 * - Preview del logo en escala de grises (solo visual; al pedido llega el original).
 * - Cuando la opción tiene cargo, agrega al carro el producto de cobro. Los IDs
 *   de variante los inyecta el tema (resueltos por handle en
 *   engraving-options.liquid), así no hay que copiar IDs a mano.
 */

class EngravingOptions extends HTMLElement {
  constructor() {
    super();
    this.tier = parseInt(this.dataset.tier, 10) || 0;
    this.textVariantId = this.dataset.textVariant || '';
    this.imageVariantId = this.dataset.imageVariant || '';
    this.form = this.closest('form');
    this.submitButton = document.getElementById(this.dataset.submitButton);

    this.radios = this.querySelectorAll('.engraving__radio');
    this.textFields = this.querySelector('.engraving__fields--text');
    this.imageFields = this.querySelector('.engraving__fields--image');
    this.textInput = this.querySelector('.engraving__text');
    this.fileInput = this.querySelector('.engraving__file');
    this.counter = this.querySelector('[data-count]');
    this.errorEl = this.querySelector('.engraving__error');
    this.previewWrap = this.querySelector('.engraving__preview');
    this.canvas = this.querySelector('.engraving__canvas');

    // Si el botón ya venía deshabilitado (agotado), no lo tocamos.
    this.soldOut = this.submitButton ? this.submitButton.disabled : false;
  }

  connectedCallback() {
    if (!this.form || !this.submitButton) return;

    if (!this.soldOut) this.setSubmitEnabled(false);

    // Campos deshabilitados de inicio: así no viajan propiedades vacías
    // en pedidos sin grabado (los inputs disabled no entran al FormData).
    this.setFieldsEnabled(null);

    this.radios.forEach((r) =>
      r.addEventListener('change', () => this.onChoiceChange())
    );

    if (this.textInput) {
      this.textInput.addEventListener('input', () => this.onTextInput());
      // Cortamos el Enter que crearía una tercera línea, en vez de dejar que
      // la escriba y recién ahí rechazarla.
      this.textInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && this.textInput.value.split('\n').length >= 2) {
          e.preventDefault();
        }
      });
    }

    if (this.fileInput) {
      this.fileInput.addEventListener('change', () => this.onFileChange());
    }

    // Interceptamos el submit para poder validar y, si aplica, agregar el
    // producto de cobro. Fase de captura para adelantarnos al handler del tema.
    this.form.addEventListener('submit', (e) => this.onSubmit(e), true);
  }

  // 'none' | 'text' | 'image' | null (sin elegir)
  get choice() {
    const checked = this.querySelector('.engraving__radio:checked');
    return checked ? checked.dataset.choice : null;
  }

  // Cargo que corresponde a la opción elegida.
  get chargeVariantId() {
    if (this.choice === 'image') return this.imageVariantId;
    if (this.choice === 'text' && this.tier > 0) return this.textVariantId;
    return '';
  }

  onChoiceChange() {
    const choice = this.choice;
    if (this.textFields) this.textFields.hidden = choice !== 'text';
    if (this.imageFields) this.imageFields.hidden = choice !== 'image';
    this.setFieldsEnabled(choice);
    if (this.errorEl) this.errorEl.hidden = true;
    if (!this.soldOut) this.setSubmitEnabled(choice !== null);
  }

  // Solo habilita los campos de la opción elegida; los demás quedan fuera
  // del FormData para que no lleguen propiedades vacías al pedido.
  setFieldsEnabled(choice) {
    if (this.textInput) this.textInput.disabled = choice !== 'text';
    if (this.fileInput) this.fileInput.disabled = choice !== 'image';
  }

  setSubmitEnabled(enabled) {
    this.submitButton.disabled = !enabled;
  }

  showError(msg) {
    if (!this.errorEl) return;
    if (msg) this.errorEl.textContent = msg;
    this.errorEl.hidden = false;
  }

  // Devuelve un mensaje de error si el texto no cumple las reglas, o null si va bien.
  // Reglas: máximo 2 líneas y 40 caracteres. (El vacío se maneja aparte.)
  textError(value) {
    const t = (value || '').trim();
    if (t.length === 0) return null;
    if (t.split('\n').length > 2) return 'El texto no puede tener más de 2 líneas.';
    if (t.length > 40) return 'El texto no puede superar los 40 caracteres.';
    return null;
  }

  onTextInput() {
    const value = this.textInput.value;
    if (this.counter) this.counter.textContent = value.length;
    const err = this.textError(value);
    if (err) {
      this.showError(err);
      if (!this.soldOut) this.setSubmitEnabled(false);
    } else {
      if (this.errorEl) this.errorEl.hidden = true;
      if (!this.soldOut) this.setSubmitEnabled(this.choice !== null);
    }
  }

  onFileChange() {
    if (this.errorEl) this.errorEl.hidden = true;
    this.renderGrayscalePreview();
  }

  renderGrayscalePreview() {
    const file = this.fileInput.files && this.fileInput.files[0];
    if (!file || !this.canvas) {
      if (this.previewWrap) this.previewWrap.hidden = true;
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const ctx = this.canvas.getContext('2d');
      const size = this.canvas.width;
      ctx.clearRect(0, 0, size, size);
      // Encajar la imagen dentro del canvas manteniendo proporción.
      const scale = Math.min(size / img.width, size / img.height);
      const w = img.width * scale;
      const h = img.height * scale;
      ctx.filter = 'grayscale(1) contrast(1.1)';
      ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
      if (this.previewWrap) this.previewWrap.hidden = false;
      URL.revokeObjectURL(url);
    };
    img.src = url;
  }

  block(e, msg) {
    e.preventDefault();
    e.stopImmediatePropagation();
    this.showError(msg);
  }

  onSubmit(e) {
    const choice = this.choice;

    // 0) Garantía: el campo de la opción elegida DEBE estar habilitado al
    //    serializar, o su valor no viaja y el pedido llega sin el dato. No
    //    basta con habilitarlo al elegir la tarjeta: si algo reinserta este
    //    elemento en el DOM, connectedCallback vuelve a deshabilitarlo. Lo
    //    reafirmamos aquí, que es el único momento que importa.
    this.setFieldsEnabled(choice);

    // 1) Sin elección => bloquear.
    if (choice === null) {
      this.block(e, 'Elige una opción de personalización para continuar.');
      return;
    }

    // 2) Grabado de texto => texto obligatorio y dentro de las reglas.
    if (choice === 'text') {
      const value = this.textInput ? this.textInput.value : '';
      if (value.trim().length === 0) {
        this.block(e, 'Escribe el texto a grabar.');
        return;
      }
      const textErr = this.textError(value);
      if (textErr) {
        this.block(e, textErr);
        return;
      }
      // Normalizamos antes de enviar para que al pedido no lleguen espacios
      // sobrantes. Va aquí porque cubre los dos caminos: el envío nativo del
      // tema (grabado gratis) y el que agrega el producto de cobro.
      this.textInput.value = value.trim();
    }

    // 3) Grabado de imagen => archivo obligatorio.
    if (choice === 'image') {
      const hasFile = this.fileInput && this.fileInput.files.length > 0;
      if (!hasFile) {
        this.block(e, 'Sube la imagen o logo que quieres grabar.');
        return;
      }
    }

    // 4) Sin cargo (no personalizar, o texto en producto gratis) => flujo normal
    //    del tema (notificación nativa). No interceptamos.
    const variantId = this.chargeVariantId;
    if (!variantId) return;

    // 5) Con cargo => agregamos producto + cobro y vamos al carro.
    e.preventDefault();
    e.stopImmediatePropagation();
    this.addWithCompanion(variantId);
  }

  async addWithCompanion(variantId) {
    this.setSubmitEnabled(false);
    this.submitButton.classList.add('loading');

    try {
      // Token compartido que enlaza el cuchillo con su grabado en el carro.
      const token = 'g' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
      const qtyInput = this.form.querySelector('[name="quantity"]');
      const qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

      // a) Agregar el cuchillo con sus propiedades (incluye archivo si hay) + token.
      //    Las propiedades de texto se reescriben explícitamente en vez de
      //    confiar en lo que haya serializado el formulario: es la diferencia
      //    entre un pedido con el grabado y uno que llega vacío al taller.
      const knifeData = new FormData(this.form);
      const checked = this.querySelector('.engraving__radio:checked');
      if (checked) knifeData.set('properties[Personalización]', checked.value);
      if (this.choice === 'text' && this.textInput) {
        knifeData.set('properties[Texto a grabar]', this.textInput.value.trim());
      }
      knifeData.append('properties[_grabado_id]', token);
      await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        body: knifeData,
      });

      // b) Agregar el servicio de grabado, misma cantidad y mismo token.
      //    Solo lleva el token (oculto): el detalle de qué grabar ya viaja en
      //    la línea del cuchillo, y repetirlo lo duplicaba en el correo. El
      //    token además es distinto en cada compra, así que dos líneas de
      //    cobro nunca se fusionan entre sí.
      await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: Number(variantId),
              quantity: qty,
              properties: { _grabado_id: token },
            },
          ],
        }),
      });

      window.location = `${window.Shopify.routes.root}cart`;
    } catch (err) {
      console.error('[grabado] Error al agregar al carro', err);
      this.showError('Hubo un problema al agregar el grabado. Intenta de nuevo.');
      this.setSubmitEnabled(true);
      this.submitButton.classList.remove('loading');
    }
  }
}

customElements.define('engraving-options', EngravingOptions);